import { Check, Plus, ChevronRight, MapPinOff, CalendarCheck2 } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusDot } from "@/components/StatusPill";
import { Skeleton } from "@/components/ui/skeleton";
import { KIND_LABEL } from "@/lib/calendar";
import { formatDate, daysText } from "@/lib/format";
import { haversineKm, formatKm } from "@/lib/geo";
import { cn } from "@/lib/utils";

/**
 * MapList – maskiner i utvalet, sortert etter hast (eller avstand når posisjon er kjend).
 * Klikk på rad = flytt kartet + opne detaljar. Pluss/hake = legg i tur.
 */
export const MapList = ({ machines, unlocated = [], loading, userPos, activeId, selection, onOpen, onToggleSelect, sortBy = "urgency" }) => {
  const rows = [...machines];
  if (sortBy === "distance" && userPos) {
    rows.sort((a, b) => (haversineKm(userPos, a.position) ?? 1e9) - (haversineKm(userPos, b.position) ?? 1e9));
  }

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[60px] w-full rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="p-2" data-testid="map-list">
      {rows.length === 0 ? (
        <p className="px-2 py-8 text-center text-sm text-muted-foreground" data-testid="map-list-empty">Ingen maskiner i utvalet. Utvid tidsrommet eller endre filter.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((m) => {
            const sel = selection?.get(m.id);
            const dist = userPos ? haversineKm(userPos, m.position) : null;
            const active = activeId === m.id;
            return (
              <li key={m.id} className={cn("flex items-center gap-1.5 rounded-xl border bg-card p-1.5 transition-colors", active && "border-primary/60 bg-primary/5")} data-testid={`map-list-row-${m.id}`}>
                <button
                  type="button"
                  onClick={() => onToggleSelect?.(m)}
                  aria-pressed={!!sel}
                  aria-label={sel ? "Fjern frå tur" : "Legg i tur"}
                  className={cn(
                    "grid h-10 w-10 shrink-0 place-items-center rounded-lg border text-xs font-bold transition-colors",
                    sel ? "border-primary bg-primary text-[#0B0D10]" : "border-border bg-muted text-muted-foreground hover:bg-accent"
                  )}
                  data-testid={`map-list-toggle-${m.id}`}
                >
                  {sel ? sel.order || <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </button>
                <button type="button" onClick={() => onOpen?.(m)} className="pressable flex min-h-[48px] min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1.5 text-left hover:bg-accent" data-testid={`map-list-open-${m.id}`}>
                  <StatusDot status={m.overall_status} className="h-3 w-3" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-semibold">{m.label}</span>
                      {m.is_planned && <CalendarCheck2 className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--status-planned))] dark:text-slate-300" aria-label="Planlagt" />}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {m.customer_name}
                      {m.next ? ` · ${KIND_LABEL[m.next.kind]} ${formatDate(m.next.date)} · ${daysText(m.next.days_remaining)}` : ""}
                    </span>
                  </span>
                  {dist != null && <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">{formatKm(dist)}</span>}
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {unlocated.length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed p-3 text-xs text-muted-foreground" data-testid="map-unlocated">
          <div className="mb-1 flex items-center gap-1.5 font-semibold text-foreground"><MapPinOff className="h-3.5 w-3.5" /> {unlocated.length} maskin{unlocated.length === 1 ? "" : "er"} utan plassering</div>
          <ul className="space-y-0.5">
            {unlocated.slice(0, 6).map((u) => (
              <li key={u.id} className="truncate">{u.label} · {u.customer_name || "utan kunde"}</li>
            ))}
            {unlocated.length > 6 && <li>… og {unlocated.length - 6} til</li>}
          </ul>
          <p className="mt-1.5">Legg inn adresse på <Link to="/kunder" className="font-semibold text-foreground underline-offset-2 hover:underline">kunden</Link> eller ei eiga plassering på maskina.</p>
        </div>
      )}
    </div>
  );
};
